import { request } from 'util/api';

/**
 * The listing wizard's and ManageListingsPage's calls to Sharetribe, routed through
 * `/api/v2/listings` so the server acts as the organisation's provider whoever is
 * signed in (CH-574). The method names mirror the client SDK's, so a duck swaps
 * `sdk.ownListings.show` for `listingsApi.ownListings.show` and nothing else moves.
 *
 * Transit both ways: bodies carry the SDK's Money, UUID and Date classes as they do
 * today, and responses come back as the raw SDK response shape, ready for
 * `addMarketplaceEntities`.
 */

const BASE = '/api/v2/listings';

// Ducks pass listing ids as `UUID`s or strings; exception ids are strings.
const idOf = (id) => (typeof id === 'string' ? id : id?.uuid);

const queryString = (params = {}) => {
  const entries = Object.entries(params).filter(([, v]) => v != null);
  if (entries.length === 0) return '';

  return `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`;
};

const get = (path, query) => request(`${path}${queryString(query)}`, { method: 'GET' });
const post = (path, body) => request(path, { method: 'POST', body });
const put = (path, body) => request(path, { method: 'PUT', body });

// `request` defaults to transit; the domain-shaped routes speak plain JSON.
const JSON_HEADERS = { 'Content-Type': 'application/json' };
const json = (method, path, body) => request(path, { method, headers: JSON_HEADERS, body });

// Accepts a Date, an ISO string or epoch ms.
const toIso = (value) => new Date(value).toISOString();

/**
 * Integration API responses say `type: 'listing'`; the pages look entities up as
 * `ownListing` (`EditListingPage.js` `getOwnListing`, ManageListingsPage's
 * `addOwnEntities`). Relabel the primary data so both keep working. Included
 * entities (author, images) are left alone.
 */
export const asOwnListing = (response) => {
  const data = response?.data?.data;
  if (!data) return response;

  const relabel = (entity) =>
    entity?.type === 'listing' ? { ...entity, type: 'ownListing' } : entity;

  return {
    ...response,
    data: {
      ...response.data,
      data: Array.isArray(data) ? data.map(relabel) : relabel(data),
    },
  };
};

const ownListings = {
  /**
   * `{ view, title, page, perPage }`, ManageListingsPage's filters (the legacy
   * `/api/own-listings` contract). Includes and image variants are the server's.
   */
  query: (params) => get(BASE, params).then(asOwnListing),
  show: ({ id }) => get(`${BASE}/${idOf(id)}`).then(asOwnListing),
  /** The Details tab's payload. Created as pendingApproval with the draft marker. */
  create: (body) => post(BASE, body).then(asOwnListing),
  update: ({ id, ...body }) => put(`${BASE}/${idOf(id)}`, body).then(asOwnListing),
  /** Approves the listing; the Stripe branch is decided on the server. */
  publish: ({ id }) => post(`${BASE}/${idOf(id)}/publish`).then(asOwnListing),
  /** `{ listing: { id, state } }`, not an SDK entity. */
  open: ({ id }) => json('POST', `${BASE}/${idOf(id)}/open`),
  close: ({ id }) => json('POST', `${BASE}/${idOf(id)}/close`),
  /** A new v2 draft copied from `id`: images re-uploaded, future exceptions re-created. */
  duplicate: ({ id }) => post(`${BASE}/${idOf(id)}/duplicate`).then(asOwnListing),
  /** A `pendingApproval` draft only: approved privately, then closed. `{ listing: { id, state } }`. */
  discard: ({ id }) => json('POST', `${BASE}/${idOf(id)}/discard`),
};

const images = {
  upload: ({ listingId, file }) => {
    const form = new FormData();
    form.append('image', file);
    // No transit header: the browser sets multipart with its boundary.
    return request(`${BASE}/${idOf(listingId)}/images`, {
      method: 'POST',
      headers: {},
      body: form,
    });
  },
};

/** `ListingException` (`shared/listing.ts`): `{ id, start, end, seats }` with ISO instants. */
const exceptions = {
  /** `{ exceptions }`, every page merged, sorted by start. */
  list: ({ listingId, start, end }) =>
    json(
      'GET',
      `${BASE}/${idOf(listingId)}/exceptions${queryString({ start: toIso(start), end: toIso(end) })}`
    ),
  /** `{ exception }`. */
  create: ({ listingId, start, end, seats }) =>
    json('POST', `${BASE}/${idOf(listingId)}/exceptions`, {
      start: toIso(start),
      end: toIso(end),
      seats,
    }),
  /** `{ exception: { id } }`. */
  delete: ({ listingId, id }) =>
    json('DELETE', `${BASE}/${idOf(listingId)}/exceptions/${idOf(id)}`),
  /** `{ exceptions: [{ id }] }`, the ones deleted. An id not on this listing is skipped. */
  deleteMany: ({ listingId, ids }) =>
    json('POST', `${BASE}/${idOf(listingId)}/exceptions/delete`, { ids: ids.map(idOf) }),
};

const transactions = {
  /** `{ count }`, the has-bookings check behind `useListingHasBookings`. */
  count: ({ listingId }) => get(`${BASE}/${idOf(listingId)}/bookings/count`),
};

const listingsApi = { ownListings, images, exceptions, transactions };

export default listingsApi;
